import { Component, Input } from '@angular/core';

/**
 * Visual group of a character tag or of a gastronomic affinity.
 *
 * The contract with the backend is `group -> iconKey`: a term never carries its own artwork,
 * the GROUP does, so adding `fruit.stone.apricot` to the vocabulary needs no frontend change.
 *
 * Every glyph here is drawn for SpiritsRim. No retailer badge, medal or third-party icon is
 * ever used, which is a licensing requirement and not a stylistic preference.
 *
 * Each glyph is a SILHOUETTE plus a stroked detail on a 32-unit grid, rather than a single
 * hairline path. Two reasons, both practical: at 20-24px a one-stroke diagram collapses into
 * noise, and the character tiles use the same artwork blown up to 60-96px as a watermark, where
 * a hairline disappears altogether. The silhouette is what survives both sizes.
 */
export type IconGroup =
    | 'fruit' | 'grain' | 'sweet' | 'smoke' | 'wood' | 'spice' | 'botanical'
    | 'maritime' | 'floral' | 'dairy' | 'vegetal'
    | 'fish' | 'meat' | 'cheese' | 'dessert' | 'snack' | 'vegetable';

/** `shape` is filled and outlined, `detail` is stroked only, `accent` is solid. */
export interface Glyph {
  shape:   string;
  detail:  string;
  accent:  string;
}

const GLYPHS: Record<string, Glyph> = {
  // Citrus half: rind, pith and six segment walls.
  fruit: {
    shape: 'M16 3.6c6.85 0 12.4 5.55 12.4 12.4S22.85 28.4 16 28.4 3.6 22.85 3.6 16 9.15 3.6 16 3.6Z',
    detail: 'M16 6.6a9.4 9.4 0 1 0 0 18.8 9.4 9.4 0 0 0 0-18.8ZM16 13.4V6.6M18.25 14.7 24.14 11.3M18.25 17.3 24.14 20.7M16 18.6V25.4M13.75 17.3 7.86 20.7M13.75 14.7 7.86 11.3',
    accent: 'M16 13.2a2.8 2.8 0 1 1 0 5.6 2.8 2.8 0 0 1 0-5.6Z'
  },
  // Juniper sprig: three berries held by needles.
  botanical: {
    shape: 'M16 8.4a3.1 3.1 0 1 1 0 6.2 3.1 3.1 0 0 1 0-6.2Zm-5.6 6.9a2.7 2.7 0 1 1 0 5.4 2.7 2.7 0 0 1 0-5.4Zm11.2 0a2.7 2.7 0 1 1 0 5.4 2.7 2.7 0 0 1 0-5.4Z',
    detail: 'M16 28V15M16 22.5c-2.7-.4-4.4-2-5-4.8M16 25.8c2.7-.4 4.4-2 5-4.8M16 8.4c-.6-2-.2-3.8 1.2-5.4M12.6 6.2c-1.3-.9-2.7-1-4.2-.4.5 1.5 1.5 2.4 3 2.7',
    accent: ''
  },
  // Star anise: eight carpels around a seed.
  spice: {
    shape: 'M16 4.5 17.76 11.75 24.13 7.87 20.25 14.24 27.5 16 20.25 17.76 24.13 24.13 17.76 20.25 16 27.5 14.24 20.25 7.87 24.13 11.75 17.76 4.5 16 11.75 14.24 7.87 7.87 14.24 11.75Z',
    detail: 'M16 9.5v13M9.5 16h13',
    accent: 'M16 13.6a2.4 2.4 0 1 1 0 4.8 2.4 2.4 0 0 1 0-4.8Z'
  },
  // Ear of grain.
  grain: {
    shape: 'M16 6.2c1.9 1.6 2.8 3.5 2.6 5.8-2.3.2-4.1-.8-5.2-2.9.6-1.2 1.5-2.2 2.6-2.9Zm0 6.6c1.9 1.6 2.8 3.5 2.6 5.8-2.3.2-4.1-.8-5.2-2.9.6-1.2 1.5-2.2 2.6-2.9Zm0 6.6c1.9 1.6 2.8 3.5 2.6 5.8-2.3.2-4.1-.8-5.2-2.9.6-1.2 1.5-2.2 2.6-2.9Z',
    detail: 'M13.2 28V9.6M13.2 12.6c-2.4-.5-3.8-2-4.3-4.6 2.5-.2 4.1.9 4.8 3.2M13.2 19.2c-2.4-.5-3.8-2-4.3-4.6 2.5-.2 4.1.9 4.8 3.2',
    accent: ''
  },
  // A drop of honey mid-fall.
  sweet: {
    shape: 'M16 4c4.3 5.3 6.6 9.2 6.6 12.5a6.6 6.6 0 0 1-13.2 0C9.4 13.2 11.7 9.3 16 4Z',
    detail: 'M13.2 17.6c0 2 1.2 3.4 3 3.7',
    accent: ''
  },
  // Rising smoke over an ember line.
  smoke: {
    shape: 'M6.5 25.4h19v2.2h-19z',
    detail: 'M9 21.6c3.1 0 3.1-2.9 6.2-2.9s3.1 2.9 6.2 2.9M9 15.6c3.1 0 3.1-2.9 6.2-2.9s3.1 2.9 6.2 2.9M9 9.6c3.1 0 3.1-2.9 6.2-2.9s3.1 2.9 6.2 2.9',
    accent: ''
  },
  // Cask head: staves and two hoops.
  wood: {
    shape: 'M10.5 4.5h11c1.9 3.5 2.8 7.3 2.8 11.5s-.9 8-2.8 11.5h-11C8.6 24 7.7 20.2 7.7 16s.9-8 2.8-11.5Z',
    detail: 'M6.6 11.2h18.8M6.6 20.8h18.8M16 4.5v23',
    accent: ''
  },
  // A wave with a salt crystal.
  maritime: {
    shape: 'M16 3.8l2.6 4.5h-5.2z',
    detail: 'M3.5 21.5c2.4 0 2.4-2.5 4.8-2.5s2.4 2.5 4.8 2.5 2.4-2.5 4.8-2.5 2.4 2.5 4.8 2.5 2.4-2.5 4.8-2.5M3.5 26.5c2.4 0 2.4-2.5 4.8-2.5s2.4 2.5 4.8 2.5 2.4-2.5 4.8-2.5 2.4 2.5 4.8 2.5 2.4-2.5 4.8-2.5M16 8.3v6.4',
    accent: ''
  },
  // Five-petal flower with a stamen centre.
  floral: {
    shape: 'M16 4.5 18.47 11.6 25.99 11.76 19.99 16.3 22.17 23.49 16 19.2 9.83 23.49 12.01 16.3 6.01 11.76 13.53 11.6Z',
    detail: 'M16 19.2V28',
    accent: 'M16 12.4a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z'
  },
  // Cream jug.
  dairy: {
    shape: 'M10.5 5h11l-1.3 5.6c1.7 1.3 2.6 3.2 2.6 5.6V27H9.2V16.2c0-2.4.9-4.3 2.6-5.6L10.5 5Z',
    detail: 'M9.2 16.4h13.6',
    accent: ''
  },
  // Leaf with a central vein.
  vegetal: {
    shape: 'M26 5.5c0 10.3-4.8 15.5-11 15.5-3.5 0-6-2.4-6-5.9C9 9.1 15.3 5.5 26 5.5Z',
    detail: 'M7 27c3.5-4.7 7.2-8.4 12-11.2',
    accent: ''
  },
  // Fish: body, forked tail and a dorsal fin, so it still reads as a fish at 20px.
  fish: {
    shape: 'M10.6 16c3.7-4.7 7.8-7 12.1-7 4.1 0 6.9 2.3 8 7-1.1 4.7-3.9 7-8 7-4.3 0-8.4-2.3-12.1-7Z',
    detail: 'M10.6 16 3.7 10.6v10.8L10.6 16M16 9.8c1.2-2.4 2.9-3.5 5-3.3',
    accent: 'M24.6 14.4a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2Z'
  },
  // Cut of meat with the bone.
  meat: {
    shape: 'M10 7.3c4.7-2.8 10-2 13.2 1.4s4 8.8 1.2 13.5c-2.7 4.4-8 4.8-11.6 1.8-2.1-1.7-2.1-3.7-3.7-5.2-1.6-1.5-3.5-1.6-4.5-3.7-1.2-2.4 0-5.4 2.9-6.4 1.3-.5 1.7-.9 2.5-1.4Z',
    detail: '',
    accent: 'M18.5 12.6c2.6 0 4.4 1.9 4.4 4.4s-2 4.6-4.6 4.6-4.4-1.9-4.4-4.4 2-4.6 4.6-4.6Z'
  },
  // Wedge of cheese.
  cheese: {
    shape: 'M5 19.3 24 8.7c1.9 1.1 2.7 2.8 2.7 5.3v6.6H5v-1.3Z',
    detail: 'M5 16.7h21.7',
    accent: 'M11 20.5a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8Zm7 0a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8Z'
  },
  // Coffee cup, for the sweet course.
  dessert: {
    shape: 'M6.5 11.5h16v7.6c0 3.2-2.2 5.3-5.4 5.3h-5.2c-3.2 0-5.4-2.1-5.4-5.3v-7.6Z',
    detail: 'M22.5 13.1h1.9c2 0 3.2 1.2 3.2 3.1s-1.2 3.1-3.2 3.1h-1.9M11.5 8.4c.8-1 .8-2 0-3M17 8.4c.8-1 .8-2 0-3M5.5 27.5h18',
    accent: ''
  },
  // Nut, cracked.
  snack: {
    shape: 'M16 4.5c5.3 0 8.8 4 8.8 9.7S20.6 27.5 16 27.5 7.2 20 7.2 14.2 10.7 4.5 16 4.5Z',
    detail: 'M16 6v20M11.9 10c1.9 1.9 1.9 7.5 0 9.4M20.1 10c-1.9 1.9-1.9 7.5 0 9.4',
    accent: ''
  },
  // Two herb leaves.
  vegetable: {
    shape: 'M16 27.5c0-8 3.3-13.3 10-16-.7 8.5-4 13.3-10 16Z',
    detail: 'M16 27.5c0-5.7-2.7-9.6-8-11.6.5 6.1 3.2 9.7 8 11.6',
    accent: ''
  }
};

const FALLBACK: Glyph = { shape: 'M16 5 27 16 16 27 5 16Z', detail: '', accent: '' };

@Component({
  selector: 'app-group-icon',
  standalone: true,
  template: `
    <svg [attr.width]="size" [attr.height]="size" viewBox="0 0 32 32" fill="none"
         aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">
      @if (glyph.shape) {
        <path [attr.d]="glyph.shape" fill="currentColor" [attr.fill-opacity]="solid ? 1 : 0.26"/>
        <path [attr.d]="glyph.shape" stroke="currentColor" [attr.stroke-width]="strokeWidth"
              stroke-linecap="round" stroke-linejoin="round"/>
      }
      @if (glyph.detail) {
        <path [attr.d]="glyph.detail" stroke="currentColor" [attr.stroke-width]="strokeWidth"
              stroke-linecap="round" stroke-linejoin="round"/>
      }
      @if (glyph.accent) {
        <path [attr.d]="glyph.accent" fill="currentColor"/>
      }
    </svg>
  `,
  styles: [':host { display: inline-flex; line-height: 0; }']
})
export class GroupIconComponent {

  /** An unknown group falls back to a neutral glyph: the tag still renders, it is not dropped. */
  @Input() set group(value: string | null | undefined) {
    this.glyph = (value && GLYPHS[value]) || FALLBACK;
  }

  /** Rendered px. The artwork is scale-independent; only the stroke needs to keep up. */
  @Input() size = 22;

  /** Watermark use: the silhouette carries the whole mark and the fill goes opaque. */
  @Input() solid = false;

  glyph: Glyph = FALLBACK;

  /**
   * The stroke thins as the glyph grows. A fixed 1.7 on a 96px watermark reads as a heavy
   * outline around a flat shape instead of as a drawing.
   */
  get strokeWidth(): number {
    return this.size >= 56 ? 1.1 : 1.7;
  }
}
